import {
  CatalogFilterLayout,
  useEntityOwnership,
  UserListFilter,
  useStarredEntities,
} from '@backstage/plugin-catalog-react';
import { catalogApiRef } from '@backstage/plugin-catalog-react';
import {
  Grid,
  LinearProgress,
  Button,
  useTheme,
  useMediaQuery,
} from '@material-ui/core';
import React, { useContext, useEffect, useMemo, useState } from 'react';
import AllAssetsCardGrid from '../../../home/allAssets/cardView/AllAssetsCardGrid';
import { useApi } from '@backstage/core-plugin-api';
import { useAsync } from 'react-use';
import * as constant from '../../../utils/constant';
import { AuthContext } from '../../../../providers/AuthProvider';
import TicketDetailFilters from '../Filters/TicketDetailFilters';
import { useStyles } from '../styles';
import AddIcon from '@material-ui/icons/Add';
import TicketPopup from '../popups/TicketPopup';
import { useTicketDetails } from '../hooks/useTicketDetails';
import { useTickets } from '../hooks/useTickets';
import { useFilteredAssets } from '../hooks/useFilteredAssets';

const useCatalogEntity = (parsedJson: any) => {
  const catalogApi = useApi(catalogApiRef);
  const {
    value: entity,
    error,
    loading,
  } = useAsync(() => catalogApi.getEntities());
  const reusableComponents = useMemo(() => {
    if (!parsedJson?.devx_reusable_components) {
      return [];
    }
    
    const componentsMap = Object.entries(parsedJson.devx_reusable_components).flatMap(
      ([key, components]) =>
        components.map((component: any) => ({
          ...component,
          category: key,
        }))
    );
    const uniqueComponents = new Map();
    componentsMap.forEach((component: any) => {
      if (!uniqueComponents.has(component.component_name)) {
      uniqueComponents.set(component.component_name, component);
      }
    });
    const deduplicatedComponents = Array.from(uniqueComponents.values());
    return deduplicatedComponents.map((component: any, idx) => {
      const matchedEntity = entity?.items.find(
        (item: any) => {
          return item.metadata.name === component.component_name;
        }
      );

      return {
        ...component,
        ...matchedEntity,
      };
    });
  }, [parsedJson, entity]);
  return { entity, error, loading, reusableComponents };
};

const ReusableCards = ({
  ticketId,
  ticketNumber,
  setCurrentView,
  onSelectionChange,
  resetTrigger,
  parsedJson
}) => {
  const classes = useStyles();
  const entityData = useCatalogEntity(parsedJson);
  const { entity, error, loading, reusableComponents } = entityData;
  const [selectedCards, setSelectedCards] = useState([]);
  const [newAssetRequests, setNewAssetRequests] = useState([]);
  const { assets, refreshAssets, ticketDetailsLoading } = useTicketDetails(
    ticketId,
    true,
  );
  const { saveNewAssetRequest } = useTickets(false);

  useEffect(() => {
    // Reset selected cards when resetTrigger changes
    setSelectedCards([]);
  }, [resetTrigger]);

  useEffect(() => {
    setSelectedCards(
      assets?.data?.assets?.filter(asset => !asset?.is_new_asset),
    );
    setNewAssetRequests(
      assets?.data?.assets?.filter(asset => asset?.is_new_asset),
    );
  }, [assets]);

  const emptyPopupState = {
    data: {},
    open: false,
    onClose: () => {},
    onSubmit: () => {},
    type: '',
  };

  const [popupState, setPopupState] = useState({
    ...emptyPopupState,
  });

  const handleClosePopup = () => {
    setPopupState(prevState => {
      return {
        ...prevState,
        open: false,
      };
    });
  };

  const handleSaveAssetRequest = async (type: string, formData: any) => {
    switch (type) {
      case 'newAsset':
        await saveNewAssetRequest(formData);
        await refreshAssets();
        break;
      default:
        console.log('Outside switch');
        break;
    }
    handleClosePopup();
    setCurrentView('StatusPage');
  };

  const handleNewAssetClicked = () => {
    setPopupState(prevState => {
      return {
        ...prevState,
        open: true,
        data: {
          ticketId: ticketId,
          ticketNumber: ticketNumber,
        },
        type: 'newAsset',
        onClose: handleClosePopup,
        onSubmit: handleSaveAssetRequest,
      };
    });
  };

  const initialState = {
    isCodeFilterClick: false,
    isApiFilterClick: false,
    isAllFilterClick: true,
    isFavoriteFilterClick: false,
    isIacFilterClick: false,
    isSubscribeFilterClick: false,
    isSalesforceFilterClick: false,
    isTopRatingFilterClick: false,
    isRecentlyPublishedFilterClick: false,
    isAIFilterClick: false,
    isExtensionFilterClick: false,
    isUIUXFilterClick: false,
    isPatternFilterClick: false,
    isAIAgentFilterClick: false,
  };

  const [checkClickItem, setCheckClickItem] = useState<any>(initialState);
  const { profEmail, isAdmin } = useContext(AuthContext);
  const [allAssetsData, setAllAssetsData] = useState<any>([]);
  const { isStarredEntity } = useStarredEntities();
  const theme = useTheme();
  const isMobile = useMediaQuery('(max-width: 1453px)');
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const [subscribeData, setSubscribeData] = useState<any>([]);
  const [isClickSubscribeIcon, setClickSubscribeIcon] = useState<any>(false);

  // const { isOwnedEntity } = useEntityOwnership();
  // const starredFilter = useMemo(
  //   () => new UserListFilter('starred', isOwnedEntity, isStarredEntity),
  //   [isOwnedEntity, isStarredEntity],
  // );

  // const starredFilterItem =
  //   allAssetsData &&
  //   allAssetsData.length &&
  //   allAssetsData.filter((item: any) => starredFilter.filterEntity(item));

  useEffect(() => {
    let entityItems;
    if (reusableComponents && reusableComponents.length > 0) {
      entityItems = reusableComponents;
    } else {
      entityItems = [];
    }

    setAllAssetsData(entityItems);

    localStorage.setItem('isReview', 'false');
  }, [error, loading, reusableComponents]);

  const [filterOption, setFilterOption] = useState({
    assetType: 'All Assets',
  });

  const [assetType, setAssetType] = useState<any>([]);

  useEffect(() => {
    if (allAssetsData && allAssetsData?.length > 0) {
      setAssetType([
        'All Assets',
        ...new Set(
          allAssetsData?.map((asset: any) => asset?.spec?.type).filter(Boolean),
        ),
      ]);
    }
  }, [allAssetsData]);

  const [searchText, setSearchText] = useState('');
  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchText(event.target.value);
  };

  const { filteredAssets } = useFilteredAssets(
    allAssetsData,
    filterOption,
    searchText,
  );

  useEffect(() => {
    onSelectionChange(selectedCards || []);
  }, [selectedCards]);

  const handleCheckBoxChange = (cardData: any) => {
    if (cardData.checked) {
      // Find the selected asset
      const selectedAsset = allAssetsData.find(
        (asset: any) => asset.metadata.assetstoreid === cardData.asset_store_id,
      );

      if (selectedAsset) {
        // Add new selection to array
        const newSelection = [
          ...(selectedCards || []).map(card => ({
            // Only keep necessary fields for previously selected cards
            ticket_id: card.ticket_id,
            snow_ticket_number: ticketId,
            asset_store_id: card.asset_store_id,
            status: card.status,
            component_link: '',
            is_new_asset: false,
          })),
          {
            ticket_id: ticketNumber,
            snow_ticket_number: ticketId,
            asset_store_id: selectedAsset.metadata.assetstoreid,
            status: 'Delivered',
            component_link: '',
            is_new_asset: false,
          },
        ];
        setSelectedCards(newSelection);
        // Pass the updated array to parent
        onSelectionChange(newSelection);
      }
    } else {
      // Remove unchecked asset from array
      const filteredAssets = selectedCards
        .filter(asset => asset.asset_store_id !== cardData.asset_store_id)
        .map(card => ({
          // Only keep necessary fields for previously selected cards
          ticket_id: card.ticket_id,
          snow_ticket_number: ticketId,
          asset_store_id: card.asset_store_id,
          status: card.status,
          component_link: '',
          is_new_asset: false,
        }));
      setSelectedCards(filteredAssets);
      // Pass the updated array to parent
      onSelectionChange(filteredAssets);
    }
  };

  return (
    <>
      {!loading && !ticketDetailsLoading ? (
        <>
          <Grid container spacing={0}>
            <Grid item xs={12} sm={12} md={3} lg={3}>
              <h1>Reusable assets</h1>
            </Grid>
            <Grid
              item
              xs={12}
              sm={12}
              md={9}
              lg={9}
              className={`${!isSmallScreen ? classes.flexEnd : ''} ${
                !isSmallScreen ? classes.alignItemsCenter : ''
              } ${classes.marginBottomSmall}`}
            >
              <div
                className={`${!isSmallScreen ? classes.flexEnd : ''} ${
                  classes.gap1
                }`}
              >
                <TicketDetailFilters
                  newAssetRequestCount={newAssetRequests?.length}
                  selectedCardCount={selectedCards?.length}
                  setCurrentView={setCurrentView}
                  filterOption={filterOption}
                  setFilterOption={setFilterOption}
                  assetType={assetType}
                  searchText={searchText}
                  handleSearchChange={handleSearchChange}
                />
                <Button
                  variant="contained"
                  color="primary"
                  className={isSmallScreen ? classes.width100Percent : ''}
                  classes={{ label: classes.buttonLabel }}
                  onClick={handleNewAssetClicked}
                >
                  <AddIcon />
                  <span>New Reusable Asset</span>
                </Button>
              </div>
            </Grid>
          </Grid>
          {filteredAssets.length > 0 ? (
            <AllAssetsCardGrid
              entities={filteredAssets}
              anchorElement={null}
              loading={loading}
              setAnchorElement={() => {}}
              isAdmin={isAdmin}
              checkFilterItem={checkClickItem}
              // starredFilterItem={starredFilterItem || []}
              subscribeData={subscribeData || []}
              setClickSubscribeIcon={setClickSubscribeIcon}
              isClickSubscribeIcon={isClickSubscribeIcon}
              checkbox
              onCheckBoxChange={handleCheckBoxChange}
              selectedAssets={selectedCards}
            />
          ) : <>No reusable assets found.</>}
        </>
      ) : (
        <LinearProgress />
      )}
      {popupState?.open && <TicketPopup {...popupState} />}
    </>
  );
};

export default ReusableCards;
